import { useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AbstractShape from "@/components/AbstractShape";
import { LEGAL_DOCS, LEGAL_TABS, LEGACY_SLUG_MAP } from "@/lib/legalDocs";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

// Renders an individual section. Subsections nest at most one level deep,
// so we render them inline rather than recursively (avoids babel plugin
// issues with self-referencing components).
function SectionBlock({ section, headingClass, isSub = false }) {
  const HeadingTag = isSub ? "h3" : "h2";
  return (
    <div className={isSub ? "space-y-2" : "space-y-3"}>
      <HeadingTag className={headingClass}>{section.h}</HeadingTag>
      {(section.p || []).map((para, i) => (
        <p key={`p-${i}`} className="text-[#5E5C58] leading-relaxed text-[15px]">
          {para}
        </p>
      ))}
      {section.bullets && section.bullets.length > 0 && (
        <ul className="list-disc pl-6 space-y-1.5 text-[#5E5C58] leading-relaxed text-[15px] marker:text-[#C96A4E]">
          {section.bullets.map((b, i) => (
            <li key={`b-${i}`}>{b}</li>
          ))}
        </ul>
      )}
      {(section.pAfter || []).map((para, i) => (
        <p key={`pa-${i}`} className="text-[#5E5C58] leading-relaxed text-[15px]">
          {para}
        </p>
      ))}
      {section.bulletsAfter && section.bulletsAfter.length > 0 && (
        <ul className="list-disc pl-6 space-y-1.5 text-[#5E5C58] leading-relaxed text-[15px] marker:text-[#C96A4E]">
          {section.bulletsAfter.map((b, i) => (
            <li key={`ba-${i}`}>{b}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Section({ section }) {
  const subHeading =
    "font-display text-base md:text-lg font-semibold tracking-tight text-[#2C2B29] mt-4";
  const topHeading =
    "font-display text-xl md:text-2xl font-semibold tracking-tight text-[#2C2B29]";

  return (
    <div className="space-y-4">
      <SectionBlock section={section} headingClass={topHeading} />
      {section.sub && section.sub.length > 0 && (
        <div className="space-y-5 border-l-2 border-[#E8E5DF] pl-5">
          {section.sub.map((s, i) => (
            <SectionBlock key={`s-${i}`} section={s} headingClass={subHeading} isSub />
          ))}
        </div>
      )}
    </div>
  );
}

function LegalDocument({ doc }) {
  return (
    <div className="bg-white rounded-3xl p-6 md:p-10 border border-[#E8E5DF] space-y-8 break-words [&_p]:break-words [&_li]:break-words">
      {doc.intro.map((para, i) => (
        <p key={`intro-${i}`} className="text-[#5E5C58] leading-relaxed text-[15px]">
          {para}
        </p>
      ))}
      <div className="border-t border-[#E8E5DF] pt-8 space-y-8">
        {doc.sections.map((s, i) => (
          <Section key={`sec-${i}`} section={s} />
        ))}
      </div>
    </div>
  );
}

export default function Legal() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Resolve slug: direct tab id, legacy slug, or default to "terms"
  const activeId = useMemo(() => {
    if (!slug) return "terms";
    if (LEGAL_DOCS[slug]) return slug;
    if (LEGACY_SLUG_MAP[slug]) return LEGACY_SLUG_MAP[slug];
    return "terms";
  }, [slug]);

  // Normalize URL so deep-links share clean URLs
  useEffect(() => {
    if (!slug || (!LEGAL_DOCS[slug] && LEGACY_SLUG_MAP[slug])) {
      navigate(`/legal/${activeId}`, { replace: true });
    }
  }, [slug, activeId, navigate]);

  const activeDoc = LEGAL_DOCS[activeId];
  const activeTab = LEGAL_TABS.find((t) => t.id === activeId);
  const legalTitle = activeTab?.label || "Legal";

  useSEO({
    title: `${legalTitle} | Melbi Wellness`,
    description: `${legalTitle} for Melbi Wellness — telemedicine platform providing clinician-guided care with licensed U.S. pharmacy fulfillment.`,
    url: `https://melbiwellness.com/legal/${activeId}`,
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: legalTitle, url: `/legal/${activeId}` },
      ]),
    ],
  });

  return (
    <div data-testid={`legal-page-${activeId}`}>
      <section className="relative">
        <AbstractShape
          variant="gradient-soft-sage"
          colors={["#C8D4CC", "#D4DCD7", "#E8D5C4"]}
          className="absolute inset-0"
          testId="legal-bg"
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 md:pt-24 md:pb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">Legal</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-[#2C2B29]">
            {activeDoc.title}
          </h1>
          <p className="mt-3 text-xs text-[#5E5C58]">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </p>
        </div>
      </section>

      {/* Tab navigation */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          role="tablist"
          aria-label="Legal documents"
          data-testid="legal-tabs"
          className="flex flex-wrap gap-2 border-b border-[#E8E5DF] pb-1"
        >
          {LEGAL_TABS.map((tab) => {
            const isActive = tab.id === activeId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`legal-tab-${tab.id}`}
                onClick={() => navigate(`/legal/${tab.slug}`)}
                className={`px-4 py-2.5 rounded-t-xl text-sm font-medium transition-all border-b-2 -mb-px ${
                  isActive
                    ? "border-[#C96A4E] text-[#2C2B29] bg-white"
                    : "border-transparent text-[#5E5C58] hover:text-[#2C2B29] hover:bg-white/60"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-20">
        <LegalDocument doc={activeDoc} />
      </section>
    </div>
  );
}
